import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack } from '@mui/icons-material';

import {
  classifyFlowgateResultError,
  useFlowgateResultSchema,
  useFlowgateValidationStats,
} from '@/api/flowgate';
import PageHeader from '@/components/PageHeader/PageHeader';
import FlowgateResultGrid, { ExportMenu } from './FlowgateResultGrid';
import FlowgateReportStep from './FlowgateReportStep';
import FlowgateDiscussion from './FlowgateDiscussion';

type InterpretationView = 'prioritize' | 'workbench' | 'discussion' | 'reports';

const FlowgateResultPage = () => {
  const { experimentId, resultId } = useParams<{
    experimentId: string;
    resultId: string;
  }>();
  const navigate = useNavigate();
  const [view, setView] = useState<InterpretationView>('prioritize');
  // Keep the workbench mounted after the first visit so switching tabs does
  // not throw away either table.
  const [workbenchSeen, setWorkbenchSeen] = useState(false);
  if (view === 'workbench' && !workbenchSeen) {
    setWorkbenchSeen(true);
  }

  const {
    data: schema,
    isLoading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema,
  } = useFlowgateResultSchema(resultId);
  const { data: validationStats } = useFlowgateValidationStats(
    schema?.has_row_id ? resultId : undefined,
  );

  const backToResults = () =>
    experimentId
      ? navigate(`/result/experiment/${experimentId}`)
      : navigate(-1);

  const workbenchCount = validationStats?.total ?? 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1680,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <PageHeader
        compact
        eyebrow="Variant interpretation"
        title={schema?.name ?? 'Result interpretation'}
        description="Filter variants, add prioritized ones to the workbench, discuss them, then generate a report."
        actions={
          <>
            <Button
              startIcon={<ArrowBack />}
              onClick={backToResults}
              variant="outlined"
              size="small"
            >
              Back to results
            </Button>
            {resultId && <ExportMenu resultId={resultId} />}
          </>
        }
      >
        {schema && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            {schema.row_count != null && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${schema.row_count.toLocaleString()} total rows`}
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={`${schema.columns.length} columns`}
            />
            <Typography variant="caption" color="text.secondary">
              Result ID · {resultId}
            </Typography>
          </Stack>
        )}
      </PageHeader>

      <Box
        sx={{
          mb: 2,
          flexShrink: 0,
          px: 0.75,
          py: 0.75,
          borderRadius: 2,
          bgcolor: 'primary.light',
        }}
      >
        <Tabs
          value={view}
          onChange={(_event, next: InterpretationView) => setView(next)}
          aria-label="Interpretation sections"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: 0.5 },
            '& .MuiTab-root': {
              minHeight: 40,
              px: 2,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 650,
              color: 'text.primary',
              opacity: 1,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
              },
              '&.Mui-selected': {
                color: 'primary.contrastText',
                bgcolor: 'primary.main',
              },
              '&.Mui-selected:hover': {
                bgcolor: 'primary.dark',
              },
            },
          }}
        >
          <Tab value="prioritize" label="Prioritization & Filtration" />
          <Tab
            value="workbench"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Workbench</span>
                <Box
                  component="span"
                  sx={{
                    minWidth: 22,
                    px: 0.75,
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'center',
                    bgcolor:
                      view === 'workbench'
                        ? 'rgba(255,255,255,0.24)'
                        : 'background.paper',
                    color: view === 'workbench' ? 'inherit' : 'primary.main',
                  }}
                >
                  {workbenchCount}
                </Box>
              </Stack>
            }
          />
          <Tab value="discussion" label="Remarks" />
          <Tab value="reports" label="Reports" />
        </Tabs>
      </Box>

      {schemaLoading && (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {schemaError &&
        (() => {
          const info = classifyFlowgateResultError(schemaError);
          return (
            <Alert
              severity={info.kind === 'not_ready' ? 'warning' : 'error'}
              action={
                info.kind === 'not_ready' || info.kind === 'unavailable' ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => refetchSchema()}
                  >
                    Retry
                  </Button>
                ) : null
              }
            >
              {info.message}
            </Alert>
          );
        })()}

      {!schemaLoading && !schemaError && schema && resultId && (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: view === 'prioritize' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <FlowgateResultGrid
              resultId={resultId}
              schema={schema}
              mode="prioritize"
              active={view === 'prioritize'}
              onOpenWorkbench={() => setView('workbench')}
            />
          </Box>
          {workbenchSeen && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: view === 'workbench' ? 'flex' : 'none',
                flexDirection: 'column',
              }}
            >
              <FlowgateResultGrid
                resultId={resultId}
                schema={schema}
                mode="workbench"
                active={view === 'workbench'}
              />
            </Box>
          )}
          {view === 'discussion' && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <FlowgateDiscussion resultId={resultId} />
            </Box>
          )}
          {view === 'reports' && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <FlowgateReportStep resultId={resultId} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FlowgateResultPage;
